/**
 * Phase 52.B indicator math — pure functions on OHLCV arrays.
 * All return arrays of {time, value} (or per-indicator specific shapes).
 */

// ── Moving averages ────────────────────────────────────────────────
export function sma(candles, period) {
  if (!candles?.length || !period) return [];
  const out = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= period) sum -= candles[i - period].close;
    if (i >= period - 1) out.push({ time: candles[i].time, value: +(sum / period).toFixed(6) });
  }
  return out;
}

function _emaSeries(values, period) {
  if (!values?.length || !period) return [];
  const k = 2 / (period + 1);
  const out = new Array(values.length);
  let prev = values[0];
  out[0] = prev;
  for (let i = 1; i < values.length; i++) {
    if (i < period) {
      prev = ((prev * i) + values[i]) / (i + 1); // seed with SMA-ish average
    } else {
      prev = (values[i] * k) + (prev * (1 - k));
    }
    out[i] = prev;
  }
  return out;
}

// ── MACD (12,26,9) — line + signal + histogram ─────────────────────
export function macd(candles, fast = 12, slow = 26, signal = 9) {
  if (!candles?.length) return { line: [], signal: [], hist: [] };
  const closes = candles.map(c => c.close);
  const ef = _emaSeries(closes, fast);
  const es = _emaSeries(closes, slow);
  const macdLine = closes.map((_, i) => (ef[i] ?? 0) - (es[i] ?? 0));
  const sig = _emaSeries(macdLine, signal);
  const line = []; const signalOut = []; const hist = [];
  for (let i = slow; i < candles.length; i++) {
    const t = candles[i].time;
    line.push({ time: t, value: +macdLine[i].toFixed(6) });
    signalOut.push({ time: t, value: +sig[i].toFixed(6) });
    hist.push({ time: t, value: +(macdLine[i] - sig[i]).toFixed(6) });
  }
  return { line, signal: signalOut, hist };
}

// ── ATR (14) — Average True Range ─────────────────────────────────
export function atr(candles, period = 14) {
  if (!candles?.length) return [];
  const trs = [];
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const prev = candles[i - 1];
    const tr = prev
      ? Math.max(c.high - c.low, Math.abs(c.high - prev.close), Math.abs(c.low - prev.close))
      : c.high - c.low;
    trs.push(tr);
  }
  const out = [];
  let atrPrev;
  for (let i = 0; i < trs.length; i++) {
    if (i < period) {
      if (i === period - 1) {
        const seed = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
        atrPrev = seed;
        out.push({ time: candles[i].time, value: +seed.toFixed(6) });
      }
      continue;
    }
    atrPrev = ((atrPrev * (period - 1)) + trs[i]) / period;
    out.push({ time: candles[i].time, value: +atrPrev.toFixed(6) });
  }
  return out;
}

// ── Bollinger Bands (20, 2) ────────────────────────────────────────
export function bollinger(candles, period = 20, mult = 2) {
  if (!candles?.length) return { mid: [], upper: [], lower: [] };
  const mid = []; const upper = []; const lower = [];
  const closes = candles.map(c => c.close);
  for (let i = period - 1; i < candles.length; i++) {
    const slice = closes.slice(i - period + 1, i + 1);
    const m = slice.reduce((a, b) => a + b, 0) / period;
    const v = slice.reduce((a, b) => a + (b - m) ** 2, 0) / period;
    const sd = Math.sqrt(v);
    const t = candles[i].time;
    mid.push({ time: t, value: +m.toFixed(6) });
    upper.push({ time: t, value: +(m + mult * sd).toFixed(6) });
    lower.push({ time: t, value: +(m - mult * sd).toFixed(6) });
  }
  return { mid, upper, lower };
}

// ── Double Bollinger Bands (Phase 52.1 G3) ─────────────────────────
// Two nested channels around the same SMA. `k1` is the inner band
// multiplier (typ. 1σ — "reversal zone") and `k2` is the outer band
// (typ. 2σ — "trend zone"). Traders read the band the price is in:
//   • outside k2 → strong trend continuation
//   • between k1 and k2 → healthy trend
//   • inside k1 → congestion / mean-reversion
// Returns 5 series keyed for the multi-series plugin.
export function doubleBollinger(candles, period = 20, k1 = 1, k2 = 2) {
  if (!candles?.length) return { mid: [], upper1: [], lower1: [], upper2: [], lower2: [] };
  const mid = []; const upper1 = []; const lower1 = []; const upper2 = []; const lower2 = [];
  const closes = candles.map(c => c.close);
  for (let i = period - 1; i < candles.length; i++) {
    const slice = closes.slice(i - period + 1, i + 1);
    const m = slice.reduce((a, b) => a + b, 0) / period;
    const v = slice.reduce((a, b) => a + (b - m) ** 2, 0) / period;
    const sd = Math.sqrt(v);
    const t = candles[i].time;
    mid.push({    time: t, value: +m.toFixed(6) });
    upper1.push({ time: t, value: +(m + k1 * sd).toFixed(6) });
    lower1.push({ time: t, value: +(m - k1 * sd).toFixed(6) });
    upper2.push({ time: t, value: +(m + k2 * sd).toFixed(6) });
    lower2.push({ time: t, value: +(m - k2 * sd).toFixed(6) });
  }
  return { mid, upper1, lower1, upper2, lower2 };
}

// ── Keltner Channel (Phase 52.1 G3) ────────────────────────────────
// Volatility-adaptive channel around an EMA using ATR for band width.
// mid   = EMA(period, close)
// upper = mid + mult * ATR(atrPeriod)
// lower = mid - mult * ATR(atrPeriod)
// The Bollinger comparison is deliberate: when BB squeezes INSIDE the
// KC bands (Bollinger < Keltner width) it's the classic "TTM squeeze"
// pre-breakout signal.
export function keltner(candles, period = 20, atrPeriod = 10, mult = 1.5) {
  if (!candles?.length) return { mid: [], upper: [], lower: [] };
  const closes = candles.map(c => c.close);
  const emaVals = _emaSeries(closes, period);
  const atrArr = atr(candles, atrPeriod);
  const atrByTime = new Map(atrArr.map(r => [r.time, r.value]));
  const mid = []; const upper = []; const lower = [];
  for (let i = period - 1; i < candles.length; i++) {
    const t = candles[i].time;
    const a = atrByTime.get(t);
    if (a == null) continue;
    const m = emaVals[i];
    mid.push({   time: t, value: +m.toFixed(6) });
    upper.push({ time: t, value: +(m + mult * a).toFixed(6) });
    lower.push({ time: t, value: +(m - mult * a).toFixed(6) });
  }
  return { mid, upper, lower };
}

// ── SuperTrend (10, 3) — trailing stop that flips direction ────────
export function supertrend(candles, period = 10, mult = 3) {
  if (!candles?.length) return [];
  const atrArr = atr(candles, period);
  const atrByTime = new Map(atrArr.map(r => [r.time, r.value]));
  const out = [];
  let dir = 1;   // 1 = uptrend (line below price), -1 = downtrend
  let prevBand = null;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const a = atrByTime.get(c.time);
    if (a == null) continue;
    const hl2 = (c.high + c.low) / 2;
    let upper = hl2 + mult * a;
    let lower = hl2 - mult * a;
    if (prevBand) {
      if (upper > prevBand.upper && candles[i - 1].close <= prevBand.upper) upper = prevBand.upper;
      if (lower < prevBand.lower && candles[i - 1].close >= prevBand.lower) lower = prevBand.lower;
    }
    if (prevBand) {
      if (dir === 1 && c.close < prevBand.lower) dir = -1;
      else if (dir === -1 && c.close > prevBand.upper) dir = 1;
    }
    prevBand = { upper, lower };
    const value = dir === 1 ? lower : upper;
    out.push({ time: c.time, value: +value.toFixed(6), direction: dir });
  }
  return out;
}

// ── ADX (14) — trend strength + directional indicators ─────────────
export function adx(candles, period = 14) {
  if (!candles || candles.length < period + 1) return { adx: [], plusDI: [], minusDI: [] };
  const trs = []; const plusDM = []; const minusDM = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i]; const p = candles[i - 1];
    const up = c.high - p.high;
    const dn = p.low - c.low;
    plusDM.push(up > dn && up > 0 ? up : 0);
    minusDM.push(dn > up && dn > 0 ? dn : 0);
    trs.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  // Wilder smoothing
  const smooth = (arr) => {
    const out = [];
    let seed = arr.slice(0, period).reduce((a, b) => a + b, 0);
    out.push(seed);
    for (let i = period; i < arr.length; i++) {
      seed = seed - (seed / period) + arr[i];
      out.push(seed);
    }
    return out;
  };
  const trS = smooth(trs);
  const pdmS = smooth(plusDM);
  const mdmS = smooth(minusDM);
  const plusDI = []; const minusDI = []; const dx = [];
  for (let i = 0; i < trS.length; i++) {
    if (trS[i] === 0) { plusDI.push(0); minusDI.push(0); dx.push(0); continue; }
    const p = 100 * (pdmS[i] / trS[i]);
    const m = 100 * (mdmS[i] / trS[i]);
    plusDI.push(p); minusDI.push(m);
    const sum = p + m;
    dx.push(sum ? 100 * Math.abs(p - m) / sum : 0);
  }
  // ADX = Wilder-smoothed DX
  const adxArr = [];
  if (dx.length >= period) {
    let seed = dx.slice(0, period).reduce((a, b) => a + b, 0) / period;
    adxArr.push(seed);
    for (let i = period; i < dx.length; i++) {
      seed = ((seed * (period - 1)) + dx[i]) / period;
      adxArr.push(seed);
    }
  }
  // Align with candle times (skipped first `period + period` for adx).
  const offsetADX = candles.length - adxArr.length;
  const offsetDI = candles.length - plusDI.length;
  return {
    adx: adxArr.map((v, i) => ({ time: candles[i + offsetADX].time, value: +v.toFixed(2) })),
    plusDI: plusDI.map((v, i) => ({ time: candles[i + offsetDI].time, value: +v.toFixed(2) })),
    minusDI: minusDI.map((v, i) => ({ time: candles[i + offsetDI].time, value: +v.toFixed(2) })),
  };
}

// ── Volume delta + spike detection ─────────────────────────────────
export function volumeStats(candles, spikeMult = 2.5, window = 20) {
  if (!candles?.length) return { delta: [], spikes: [], avg: [] };
  const delta = []; const spikes = []; const avg = [];
  let sumV = 0; const q = [];
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    q.push(c.volume || 0);
    sumV += c.volume || 0;
    if (q.length > window) sumV -= q.shift();
    const mean = sumV / q.length;
    avg.push({ time: c.time, value: +mean.toFixed(6) });
    delta.push({
      time: c.time,
      value: (c.close >= c.open ? 1 : -1) * (c.volume || 0),
    });
    if (i >= window && (c.volume || 0) > mean * spikeMult) {
      spikes.push({ time: c.time, ratio: mean ? (c.volume / mean) : 0, volume: c.volume });
    }
  }
  return { delta, spikes, avg };
}

// ── RVOL (Relative Volume) — TradingView built-in parity ──────────
// TradingView's built-in `Relative Volume` indicator divides the
// current bar volume by the SMA of the last `window` bars INCLUDING
// the current bar. This is different from LazyBear's "Simple RVOL"
// which excludes the current bar — but the built-in is what most
// operators actually see on TradingView.
export function rvol(candles, window = 20) {
  if (!candles?.length) return [];
  const out = [];
  const q = []; // rolling window of the PREVIOUS `window` bars — excludes current
  let sumV = 0;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    // Compute RVOL BEFORE inserting the current bar so the average
    // reflects only the prior `window` closed bars — this matches
    // TradingView's `Volume / SMA(Volume, N)[1]` convention and fixes
    // the ~5-10% under-reading operators saw on the ETH 1m chart.
    if (q.length < window) {
      out.push({ time: c.time, value: null, volume: c.volume, bull: c.close >= c.open });
    } else {
      const mean = sumV / window;
      const v = mean > 0 ? (c.volume || 0) / mean : 0;
      out.push({ time: c.time, value: +v.toFixed(3), volume: c.volume, bull: c.close >= c.open });
    }
    q.push(c.volume || 0);
    sumV += c.volume || 0;
    if (q.length > window) sumV -= q.shift();
  }
  return out;
}
